import React from 'react';
import { MARKET_PRICES, PROTOCOL_CONSTANTS } from '../constants/pricing';

const GrowthRedemptionsModal = ({ isOpen, onClose, profileData }) => {
  if (!isOpen) return null;

  const balances = profileData.balances || {};
  const nIcpBalance = balances.nICP || 0;
  const withdrawalRequests = profileData.withdrawalRequests || [];
  
  // Calculate total locked nICP from withdrawal requests
  const totalLockedNICP = withdrawalRequests.reduce((total, request) => total + request.nIcpBurned, 0);
  const totalNIcpIncludingLocked = nIcpBalance + totalLockedNICP;
  
  // Calculate total ICP due from withdrawal requests
  const totalICPDue = withdrawalRequests.reduce((total, request) => total + request.icpDue, 0);
  
  // Calculate earnings for different time periods
  const baseRedemptionValue = totalNIcpIncludingLocked * PROTOCOL_CONSTANTS.N_ICP_NAV_OVER_ICP_PROTOCOL;
  const monthlyEarningsICP = baseRedemptionValue * (PROTOCOL_CONSTANTS.STAKING_APY / 100 / 12);
  const sixMonthEarningsICP = baseRedemptionValue * (PROTOCOL_CONSTANTS.STAKING_APY / 100 / 2);
  const annualEarningsICP = baseRedemptionValue * (PROTOCOL_CONSTANTS.STAKING_APY / 100);
  const totalEarningsICP = annualEarningsICP; // Can be adjusted to show lifetime earnings
  
  // Find the earliest completion date for withdrawal requests
  const earliestCompletionDate = withdrawalRequests.length > 0 
    ? new Date(Math.min(...withdrawalRequests.map(r => new Date(r.startDate).getTime() + (r.daysLeft * 24 * 60 * 60 * 1000))))
    : null;
  
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="growth-redemptions-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">nICP earnings</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Time Period Earnings */}
        <div className="earnings-periods">
          <div className="earnings-period-item">
            <span className="period-label">1M</span>
            <span className="period-value">+{monthlyEarningsICP.toFixed(1)} ICP</span>
          </div>
          <div className="earnings-period-item">
            <span className="period-label">6M</span>
            <span className="period-value">+{sixMonthEarningsICP.toFixed(1)} ICP</span>
          </div>
          <div className="earnings-period-item">
            <span className="period-label">1Y</span>
            <span className="period-value">+{annualEarningsICP.toFixed(0)} ICP</span>
          </div>
          <div className="earnings-period-item">
            <span className="period-label">Total</span>
            <span className="period-value">+{totalEarningsICP.toFixed(0)} ICP</span>
          </div>
        </div>

        <div className="modal-body">
          
          {/* Growth Chart Placeholder */}
          <div className="growth-chart-section">
            <div className="chart-placeholder">
              [Growth Chart Placeholder]
            </div>
          </div>

          {/* Earnings Breakdown */}
          <div className="earnings-breakdown-section">
            <h4>Earnings Breakdown</h4>
            <div className="earnings-details">
              <span className="auto-yield">Auto yield: +{annualEarningsICP.toFixed(0)} ICP (last 12 months, ~{PROTOCOL_CONSTANTS.STAKING_APY}% APY)</span>
              <span className="variation">Variation: avg {PROTOCOL_CONSTANTS.STAKING_APY}%, latest epoch {(PROTOCOL_CONSTANTS.STAKING_APY - 0.2).toFixed(1)}%</span>
            </div>
          </div>

          {/* Exit Options */}
          <div className="exit-options-section">
            <h4>Exit Options</h4>
            <div className="exit-option">
              <div className="option-description">
                <span className="option-type">Redeem:</span>
                <span className="option-details">
                  {totalLockedNICP > 0 && (
                    `${totalLockedNICP.toFixed(0)} nICP queued → ${earliestCompletionDate ? `~${formatDate(earliestCompletionDate)}` : 'Jun 3'} (≈ $${(totalICPDue * MARKET_PRICES.ICP).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ICP + yield)`
                  )}
                  {totalLockedNICP === 0 && `Available for ${nIcpBalance.toFixed(0)} nICP → ~6 months (≈ $${(nIcpBalance * PROTOCOL_CONSTANTS.N_ICP_NAV_OVER_ICP_PROTOCOL * MARKET_PRICES.ICP).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ICP + yield)`}
                </span>
              </div>
              <div className="option-actions">
                <button className="redeem-more-btn">Redeem more</button>
                <button className="trade-btn">Trade</button>
              </div>
            </div>
            
            {nIcpBalance > 0 && (
              <div className="exit-option">
                <div className="option-description">
                  <span className="option-type">Trade:</span>
                  <span className="option-details">{nIcpBalance.toFixed(0)} nICP available → swap anytime on market</span>
                </div>
              </div>
            )}
          </div>

          {/* How it works */}
          <div className="how-it-works-section">
            <h4>How it works:</h4>
            <div className="how-it-works-list">
              <div className="how-it-works-item">- Yield comes from validator rewards, auto-distributed</div>
              <div className="how-it-works-item">- Redemption takes ~6 months due to staking mechanics</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrowthRedemptionsModal;
